import {useEffect, useRef, createRef, MutableRefObject, RefObject} from 'react';

const useOpenAndClosedTransition = (isOpen: boolean): RefObject<HTMLDivElement> => {
  const transitionTimeoutRef: MutableRefObject<number | undefined> = useRef();
  const elementRef: RefObject<HTMLDivElement> = createRef();

  /**
   * takes care of the closed -> open transition
   */
  useEffect(() => {
    const item: HTMLDivElement | undefined = elementRef.current || undefined;
    if (!item) {
      return () => {
        // noop
      };
    }
    clearTimeout(transitionTimeoutRef.current);
    if (isOpen) {
      // Using requestAnimationFrame makes sure that all commands happened before the browser paints
      requestAnimationFrame(() => {
        // Avoid animations so we make absolutely sure the user won't see anything happening
        item.style.transition = 'none';
        // Sets the height to auto so the browser opens the container to the desired size
        item.style.height = 'auto';
        // We grab the height information to use later
        const height = item.offsetHeight;
        // Set the container height back to 0 (remember, nothing was painted yet) to be ready for transitioning
        item.style.height = '0';
        // Reapply the transition
        item.style.transition = 'all 250ms';
        // Now that we're ready, we apply the height on the next paint. Otherwise there will be no animation, just a jump.
        transitionTimeoutRef.current = window.setTimeout(() => {
          item.style.height = `${height}px`;
        }, 0);
        // set it back to auto so when the content size changes, the container changes accordingly (avoiding cropping it)
        transitionTimeoutRef.current = window.setTimeout(() => {
          item.style.height = 'auto';
        }, 300);
      });
    } else {
      item.style.height = `${item.offsetHeight}px`;
      requestAnimationFrame(() => {
        item.style.height = '0';
      });
    }
    return () => {
      clearTimeout(transitionTimeoutRef.current);
    };
  }, [isOpen]);

  return elementRef;
};

export default useOpenAndClosedTransition;
